import React, { useEffect, useState } from 'react';
import {
	Grid2,
	Paper,
	Avatar,
	Typography,
	Chip,
	Button,
	TextField,
	Box,
	Checkbox,
	MenuItem,
	TableContainer,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	TablePagination,
	Accordion,
	AccordionSummary,
	AccordionDetails,
} from '@mui/material';
import { useMainCustomStyle } from '../../assets/theme/Theme';
import SuperAdminSideNavigationBar from '../../components/SuperAdminSideNavigationBar';
import { MAIN_THEME_COLOR } from '../../assets/theme/Colors';
import axios from 'axios';
import {
	assignCustomerToClient,
	fetchWhatsappUsers,
} from '../../services/ApiService';
import { fetchAllAdmins } from '../../services/UserService';
import { getAccessToken } from '../../Utils/AuthUtil';
import { Admin } from '../../types';
import { toast } from 'react-toastify';
import { DropdownArrow } from '../../assets/icons/Icons';

interface User {
	phone: string;
	assignedAgency?: string; // Agency ID or null
}

const defaultOrgId = '69a1a9fe9de9e45ba99ab340';

const CustomerClientAssignment = () => {
	const mainDivStyle = useMainCustomStyle();
	const token = getAccessToken();
	const [users, setUsers] = useState<User[]>([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [bulkSelected, setBulkSelected] = useState<string[]>([]);
	const [selectedAgency, setSelectedAgency] = useState('');
	const [consultants, setConsultants] = useState<Admin[]>([]);
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(25);
	const [pageV2, setPageV2] = React.useState(0);
	const [rowsPerPageV2, setRowsPerPageV2] = React.useState(10);
	const [allSelected, setAllSelected] = useState(false);

	useEffect(() => {
		getAllWhatsappUsers();
		getAllClients();
	}, []);

	async function getAllWhatsappUsers() {
		const data = await fetchWhatsappUsers();
		const list = data.whatsapp_users;
		setUsers(
			list.map((user: { user_id: string; org_id: string }) => ({
				phone: user.user_id,
				assignedAgency: user.org_id,
			})),
		);
	}

	async function getAllClients() {
		const data = await fetchAllAdmins(token ? token : '');
		setConsultants(data.content.reverse());
	}

	const filteredUsers = users.filter(user => user.phone.includes(searchTerm));

	// Stats
	const unassignedCount = users.filter(u => !u.assignedAgency).length;
	const assignedCount = users.length - unassignedCount;
	const consultantCounts: Record<string, number> = {};
	users.forEach(u => {
		if (u.assignedAgency) {
			consultantCounts[u.assignedAgency] =
				(consultantCounts[u.assignedAgency] || 0) + 1;
		}
	});

	const handleUserSelect = (phone: string, checked: boolean) => {
		if (checked) {
			setBulkSelected([...bulkSelected, phone]);
		} else {
			setBulkSelected(bulkSelected.filter(id => id !== phone));
		}
	};

	const handleAssignConsultant = (consultantId: string) => {
		const agencyId =
			consultants.find(c => c.id === consultantId)?.agencyId || '';
		setSelectedAgency(agencyId);
	};

	const handleSave = () => {
		const promises = bulkSelected.map(u =>
			assignCustomerToClient(u, selectedAgency),
		);
		Promise.all(promises)
			.then(() => {
				const updatedUsers = users.map(user =>
					bulkSelected.includes(user.phone)
						? {
								...user,
								assignedAgency: selectedAgency || undefined,
							}
						: user,
				);

				setUsers(updatedUsers);
				setSelectedAgency('');
				setBulkSelected([]);
				toast.success('All customers assigned successfully');
			})
			.catch(err => toast.error(err.message));
	};

	function handleSelectAll(selected: boolean) {
		setAllSelected(!selected);
		if (selected) {
			setBulkSelected([]);
		} else {
			const users = paginatedUsers
				.filter(user => {
					return (
						!user.assignedAgency ||
						user.assignedAgency === defaultOrgId
					);
				})
				.map(user => user.phone);
			setBulkSelected(users);
		}
	}

	const consultantMap = React.useMemo(() => {
		return consultants.reduce(
			(acc, c) => {
				acc[c.agencyId] = c.username;
				return acc;
			},
			{} as Record<string, string>,
		);
	}, [consultants]);

	async function onAssignClient(value: string, selectedId: string) {
		const response = await assignCustomerToClient(selectedId, value);
		if (response.success) {
			const updatedUsers = users.map(u =>
				u.phone === selectedId
					? {
							...u,
							assignedAgency: value || undefined,
						}
					: u,
			);
			setUsers(updatedUsers);
			toast.success(`Customer assigned Successfully`);
		} else {
			toast.error(response.message);
		}
	}

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleChangeClientPage = (event: unknown, newPage: number) => {
		setPageV2(newPage);
	};

	const handleChangeClientRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setRowsPerPageV2(parseInt(event.target.value, 10));
		setPageV2(0);
	};

	const paginatedUsers = React.useMemo(() => {
		const start = page * rowsPerPage;
		return filteredUsers.slice(start, start + rowsPerPage);
	}, [filteredUsers, page, rowsPerPage]);

	const paginatedClients = React.useMemo(() => {
		const start = pageV2 * rowsPerPageV2;
		return consultants.slice(start, start + rowsPerPageV2);
	}, [consultants, pageV2, rowsPerPageV2]);

	return (
		<>
			<SuperAdminSideNavigationBar />
			<div style={mainDivStyle}>
				<Grid2 container paddingLeft={'20px'} paddingRight={'20px'}>
					<Grid2 component={'div'} paddingLeft="30px">
						<Typography
							variant="h6"
							fontWeight={'600'}
							color="#28696D">
							Customer Assignment Dashboard
						</Typography>
					</Grid2>
					<Grid2 container width={'100%'} paddingLeft="30px">
						<Paper sx={{ p: 4, width: '100%', marginTop: '30px' }}>
							<Box sx={{ mb: 4 }}>
								<Grid2
									container
									spacing={1}
									sx={{
										mb: 3,
										justifyContent: 'space-between',
									}}>
									<Grid2>
										<Chip
											label={`Total: ${users.length}`}
											color="primary"
											size="medium"
										/>

										<Chip
											label={`Unassigned: ${unassignedCount}`}
											color="warning"
											size="medium"
											sx={{ marginX: '5px' }}
										/>

										<Chip
											label={`Assigned: ${assignedCount}`}
											color="success"
											size="medium"
										/>
									</Grid2>
									<Grid2 size={3}>
										<TextField
											fullWidth
											placeholder="Search customers..."
											value={searchTerm}
											onChange={e =>
												setSearchTerm(e.target.value)
											}
											size="small"
										/>
									</Grid2>
								</Grid2>
								{bulkSelected.length > 0 && (
									<Box
										sx={{
											mb: 3,
											p: 2,
											bgcolor: 'primary.50',
											borderRadius: 2,
										}}>
										<Typography variant="h6" sx={{ mb: 1 }}>
											{bulkSelected.length} customer
											selected
										</Typography>
										<Box
											sx={{
												display: 'flex',
												gap: 2,
												alignItems: 'center',
											}}>
											<TextField
												select
												label="Assign Client"
												size="small"
												sx={{ minWidth: 200 }}
												onChange={e =>
													handleAssignConsultant(
														e.target.value,
													)
												}>
												{consultants.map(c => (
													<MenuItem
														key={c.id}
														value={c.id}>
														{c.username} (
														{consultantCounts[
															c.id
														] || 0}{' '}
														customers)
													</MenuItem>
												))}
											</TextField>
											<Button
												variant="contained"
												sx={{
													backgroundColor:
														MAIN_THEME_COLOR,
													color: '#FFF',
												}}
												onClick={handleSave}>
												Save All Changes
											</Button>
											<Button
												variant="outlined"
												onClick={() =>
													setBulkSelected([])
												}>
												Clear Selection
											</Button>
										</Box>
									</Box>
								)}
							</Box>
							<TableContainer sx={{ maxHeight: 600 }}>
								<Table stickyHeader size="medium">
									<TableHead>
										<TableRow
											sx={{
												'& .MuiTableCell-root': {
													bgcolor:
														'background.default',
													borderBottom: '1px solid',
													borderColor: 'divider',
													fontWeight: 600,
													fontSize: 13,
													color: 'text.secondary',
												},
											}}>
											<TableCell padding="checkbox">
												<Checkbox
													size="small"
													indeterminate={
														bulkSelected.length >
															0 &&
														bulkSelected.length <
															filteredUsers.length
													}
													checked={
														filteredUsers.length >
															0 &&
														bulkSelected.length ===
															filteredUsers.length
													}
													onChange={() =>
														handleSelectAll(
															allSelected,
														)
													}
												/>
											</TableCell>
											<TableCell>Phone</TableCell>
											<TableCell>
												Assigned Client
											</TableCell>
											<TableCell align="right">
												Reassign
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{paginatedUsers.map(user => {
											const consultantName =
												user.assignedAgency
													? consultantMap[
															user.assignedAgency
														] || 'Unknown'
													: null;

											const isUnassigned =
												!consultantName ||
												consultantName === defaultOrgId;

											return (
												<TableRow
													key={user.phone}
													hover
													sx={{
														transition:
															'0.15s ease',
														'&:hover': {
															bgcolor:
																'action.hover',
														},
													}}>
													<TableCell padding="checkbox">
														<Checkbox
															size="small"
															checked={bulkSelected.includes(
																user.phone,
															)}
															onChange={e =>
																handleUserSelect(
																	user.phone,
																	e.target
																		.checked,
																)
															}
															disabled={
																!isUnassigned
															}
														/>
													</TableCell>
													<TableCell>
														<Typography
															fontWeight={500}>
															{user.phone}
														</Typography>
													</TableCell>
													<TableCell>
														{consultantName ? (
															<Chip
																label={
																	consultantName
																}
																size="small"
																sx={{
																	bgcolor:
																		'primary.50',
																	color: MAIN_THEME_COLOR,
																	fontWeight: 500,
																	borderRadius: 1,
																}}
															/>
														) : (
															<Chip
																label="Unassigned"
																size="small"
																sx={{
																	bgcolor:
																		'grey.100',
																	color: 'text.secondary',
																	fontWeight: 500,
																	borderRadius: 1,
																}}
															/>
														)}
													</TableCell>
													<TableCell align="right">
														{isUnassigned ? (
															<TextField
																select
																size="small"
																value=""
																onChange={e =>
																	onAssignClient(
																		e.target
																			.value,
																		user.phone,
																	)
																}
																sx={{
																	minWidth: 160,
																	'& .MuiInputBase-root':
																		{
																			fontSize: 14,
																		},
																}}>
																{consultants.map(
																	c => (
																		<MenuItem
																			key={
																				c.id
																			}
																			value={
																				c.agencyId
																			}>
																			{
																				c.username
																			}
																		</MenuItem>
																	),
																)}
															</TextField>
														) : (
															<Typography
																sx={{
																	bgcolor:
																		'primary.50',
																	color: '#324054',
																	fontWeight: 500,
																	borderRadius: 1,
																	fontSize: 14,
																}}>
																Client Assigned
															</Typography>
														)}
													</TableCell>
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</TableContainer>
							<TablePagination
								component="div"
								count={filteredUsers.length}
								page={page}
								onPageChange={handleChangePage}
								rowsPerPage={rowsPerPage}
								onRowsPerPageChange={handleChangeRowsPerPage}
								rowsPerPageOptions={[10, 25, 50, 100]}
							/>

							<Accordion sx={{ borderRadius: 2, my: 2 }}>
								<AccordionSummary
									expandIcon={<DropdownArrow />}
									sx={{ backgroundColor: '#FFFFFF', px: 2 }}>
									<Box
										sx={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
											width: '100%',
											marginRight: 4,
										}}>
										<Typography
											sx={{
												fontWeight: 700,
												color: MAIN_THEME_COLOR,
												fontSize: 16,
											}}>
											Client Summary
										</Typography>
										<Chip
											label={`Total: ${Object.values(
												consultantCounts,
											).reduce(
												(a, b) => a + b,
												0,
											)} customers`}
											color="success"
											variant="filled"
											size="medium"
										/>
									</Box>
								</AccordionSummary>

								<AccordionDetails sx={{ px: 0, p: 2 }}>
									<Box
										sx={{
											p: 2,
											bgcolor: 'grey.50',
											borderRadius: 3,
											border: '1px solid',
											borderColor: 'grey.200',
										}}>
										<TableContainer>
											<Table>
												<TableHead>
													<TableRow>
														<TableCell>
															Client
														</TableCell>
														<TableCell align="right">
															No of Assigned
															Customers
														</TableCell>
													</TableRow>
												</TableHead>

												<TableBody>
													{paginatedClients.map(
														consultant => {
															const count =
																consultantCounts[
																	consultant
																		.agencyId
																] || 0;

															return (
																<TableRow
																	hover
																	key={
																		consultant.id
																	}
																	sx={{
																		'&:nth-of-type(odd)':
																			{
																				backgroundColor:
																					'grey.50',
																			},
																	}}>
																	<TableCell>
																		<Box
																			sx={{
																				display:
																					'flex',
																				alignItems:
																					'center',
																				gap: 1.5,
																			}}>
																			<Avatar
																				sx={{
																					width: 32,
																					height: 32,
																				}}>
																				{
																					consultant
																						?.username?.[0]
																				}
																			</Avatar>
																			<Typography
																				fontWeight={
																					500
																				}>
																				{
																					consultant.username
																				}
																			</Typography>
																		</Box>
																	</TableCell>

																	<TableCell align="right">
																		<Box
																			sx={{
																				display:
																					'flex',
																				justifyContent:
																					'flex-end',
																				alignItems:
																					'center',
																				gap: 1,
																			}}>
																			<Box
																				sx={{
																					width: 8,
																					height: 8,
																					borderRadius:
																						'50%',
																					bgcolor:
																						count >
																						0
																							? 'success.main'
																							: 'grey.400',
																				}}
																			/>
																			<Typography
																				fontWeight={
																					500
																				}>
																				{
																					count
																				}
																			</Typography>
																		</Box>
																	</TableCell>
																</TableRow>
															);
														},
													)}
												</TableBody>
											</Table>
										</TableContainer>

										<TablePagination
											component="div"
											count={consultants.length}
											page={pageV2}
											onPageChange={
												handleChangeClientPage
											}
											rowsPerPage={rowsPerPageV2}
											onRowsPerPageChange={
												handleChangeClientRowsPerPage
											}
											rowsPerPageOptions={[]}
										/>
									</Box>
								</AccordionDetails>
							</Accordion>
						</Paper>
					</Grid2>
				</Grid2>
			</div>
		</>
	);
};

export default CustomerClientAssignment;
